import React from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";

import colors from "../../../../../utils/colors";
import { ERROR_COLOR, TEXT_PRIMARY } from "../wafEventDrawerShared";
import {
  spGridCellSx,
  spNetworkFieldLabelSx,
  spAbuseScoreTitleSx,
  spAbuseScoreCardSx,
  SP,
  spLh,
} from "./smartParseDrawerUi";

function scoreColors(score) {
  if (score > 70) {
    return { text: ERROR_COLOR, bar: ERROR_COLOR };
  }
  if (score > 40) {
    return { text: "#92400E", bar: "#CA8A04" };
  }
  return { text: colors.green[300], bar: "#16A34A" };
}

function GridCell({ label, value }) {
  const display = value != null && value !== "" ? String(value) : "—";

  return (
    <Box sx={{ ...spGridCellSx, minWidth: 0 }}>
      <Typography sx={{ ...spNetworkFieldLabelSx, letterSpacing: "0.04em" }}>
        {label}
      </Typography>
      <Typography
        title={display !== "—" ? display : undefined}
        sx={{
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.3,
          color: display === "—" ? colors.neutral[500] : TEXT_PRIMARY,
          minWidth: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {display}
      </Typography>
    </Box>
  );
}

function resolveCountryCode(reputation, countryFallback) {
  const fromRep = reputation?.country_code;
  if (fromRep != null && String(fromRep).trim() !== "") return String(fromRep).trim();
  if (countryFallback != null && String(countryFallback).trim() !== "") {
    return String(countryFallback).trim();
  }
  return null;
}

export default function AbuseConfidenceScoreCard({ reputation, countryFallback }) {
  const rawScore = reputation?.abuse_confidence_score;
  const abuseScore = rawScore != null && rawScore !== "" ? Number(rawScore) : 0;
  const score = Number.isFinite(abuseScore) ? abuseScore : 0;
  const { text: scoreColor, bar: barColor } = scoreColors(score);
  const countryCode = resolveCountryCode(reputation, countryFallback);

  return (
    <Box sx={spAbuseScoreCardSx}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography sx={spAbuseScoreTitleSx}>
            Abuse Confidence Score
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              lineHeight: spLh.px21,
              color: scoreColor,
              flexShrink: 0,
            }}
          >
            {score}%
          </Typography>
        </Stack>

        <Box sx={{ height: 8, backgroundColor: "#F3F4F6", borderRadius: "8px", overflow: "hidden" }}>
          <Box
            sx={{
              width: `${Math.min(Math.max(score, 0), 100)}%`,
              height: "100%",
              backgroundColor: barColor,
              borderRadius: "8px",
              transition: "width 0.3s ease",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: `${SP.kvGridGap}px`,
          }}
        >
          <GridCell label="ISP" value={reputation?.isp} />
          <GridCell label="Usage Type" value={reputation?.usage_type} />
          <GridCell label="Country Code" value={countryCode} />
          <GridCell
            label="Total Reports"
            value={
              reputation?.total_reports != null && reputation?.total_reports !== ""
                ? String(reputation.total_reports)
                : null
            }
          />
        </Box>
      </Stack>
    </Box>
  );
}

AbuseConfidenceScoreCard.propTypes = {
  reputation: PropTypes.object,
  countryFallback: PropTypes.string,
};
